"use client";

import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { DecisionTreeQuestionnaire } from "@/components/checklist/DecisionTreeQuestionnaire";
import { PersonalizedChecklist } from "@/components/checklist/PersonalizedChecklist";
import { type QuestionnaireAnswers, QUESTIONNAIRE_ANSWERS_KEY, generateTaskList } from "@/lib/checklist-questionnaire";

interface ChecklistClientProps {
  userId: string | null;
  initialQuestionnaireData?: {
    answers: QuestionnaireAnswers;
    completed_at: string | null;
  } | null;
}

type ViewState = "loading" | "questionnaire" | "save_prompt" | "checklist";

export function ChecklistClient({ userId, initialQuestionnaireData }: ChecklistClientProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [viewState, setViewState] = useState<ViewState>("loading");
  const [answers, setAnswers] = useState<QuestionnaireAnswers>({});
  const [_isSaving, setIsSaving] = useState(false);

  // Handle saving pending questionnaire data after login/signup
  useEffect(() => {
    const savePending = searchParams.get("save_pending");
    if (!userId || savePending !== "true") return;

    // Check for pending guest data in localStorage
    if (typeof window === "undefined") return;

    const guestKey = `${QUESTIONNAIRE_ANSWERS_KEY}:guest`;
    const guestCompletedKey = `${QUESTIONNAIRE_ANSWERS_KEY}:guest:completed`;
    const stored = window.localStorage.getItem(guestKey);
    const wasCompleted = window.localStorage.getItem(guestCompletedKey) === "true";

    if (!stored || !wasCompleted) return;

    try {
      const pendingAnswers = JSON.parse(stored) as QuestionnaireAnswers;
      if (Object.keys(pendingAnswers).length === 0) return;

      // Save to database
      const savePendingData = async () => {
        setIsSaving(true);
        try {
          const res = await fetch("/api/questionnaire", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ answers: pendingAnswers, completed: true }),
          });

          if (res.ok) {
            // Clear guest data from localStorage
            window.localStorage.removeItem(guestKey);
            window.localStorage.removeItem(guestCompletedKey);

            // Update state
            setAnswers(pendingAnswers);
            setViewState("checklist");

            // Remove the save_pending param from URL
            router.replace("/checklist", { scroll: false });
          }
        } catch (err) {
          console.error("Error saving pending questionnaire:", err);
        }
        setIsSaving(false);
      };

      savePendingData();
    } catch {
      // Ignore parse errors
    }
  }, [userId, searchParams, router]);

  // Initialize state based on user auth and questionnaire data
  useEffect(() => {
    // Skip if we're handling save_pending
    const savePending = searchParams.get("save_pending");
    if (userId && savePending === "true") {
      return; // Let the save_pending effect handle it
    }

    // If user is logged in and has completed questionnaire, show checklist
    if (userId && initialQuestionnaireData?.completed_at) {
      setAnswers(initialQuestionnaireData.answers as QuestionnaireAnswers);
      setViewState("checklist");
      return;
    }

    // If user is logged in but hasn't completed questionnaire
    if (userId && initialQuestionnaireData && !initialQuestionnaireData.completed_at) {
      setAnswers(initialQuestionnaireData.answers as QuestionnaireAnswers);
      setViewState("questionnaire");
      return;
    }

    // Check localStorage for guest progress
    if (typeof window !== "undefined") {
      const key = `${QUESTIONNAIRE_ANSWERS_KEY}:guest`;
      const stored = window.localStorage.getItem(key);

      if (stored) {
        try {
          const parsed = JSON.parse(stored) as QuestionnaireAnswers;
          // Check if questionnaire was completed (stored separately)
          const completedKey = `${QUESTIONNAIRE_ANSWERS_KEY}:guest:completed`;
          const wasCompleted = window.localStorage.getItem(completedKey) === "true";

          if (wasCompleted && Object.keys(parsed).length > 0) {
            setAnswers(parsed);
            // Show save prompt for guests who completed, checklist for logged in
            setViewState(userId ? "checklist" : "save_prompt");
            return;
          }
          if (Object.keys(parsed).length > 0) {
            // Has progress but not completed
            setAnswers(parsed);
            setViewState("questionnaire");
            return;
          }
        } catch {
          // Ignore parse errors
        }
      }
    }

    // Default to questionnaire
    setViewState("questionnaire");
  }, [userId, initialQuestionnaireData, searchParams]);

  // Handle questionnaire completion
  const handleQuestionnaireComplete = useCallback(
    async (completedAnswers: QuestionnaireAnswers) => {
      setAnswers(completedAnswers);

      // Mark as completed in localStorage for guests
      if (typeof window !== "undefined") {
        const completedKey = `${QUESTIONNAIRE_ANSWERS_KEY}:guest:completed`;
        window.localStorage.setItem(completedKey, "true");
      }

      if (userId) {
        // Save to database for authenticated users
        setIsSaving(true);
        try {
          const res = await fetch("/api/questionnaire", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ answers: completedAnswers, completed: true }),
          });

          if (!res.ok) {
            console.error("Failed to save questionnaire");
          }
        } catch (err) {
          console.error("Error saving questionnaire:", err);
        }
        setIsSaving(false);
        setViewState("checklist");
      } else {
        // Show save prompt for guests
        setViewState("save_prompt");
      }
    },
    [userId],
  );

  // Handle "Continue without saving" - go straight to checklist
  const handleContinueWithoutSaving = useCallback(() => {
    setViewState("checklist");
  }, []);

  // Handle "Save checklist" - go back to save prompt
  const handleSaveChecklist = useCallback(() => {
    setViewState("save_prompt");
  }, []);

  // Handle starting over
  const handleStartOver = useCallback(async () => {
    // Clear localStorage
    if (typeof window !== "undefined") {
      const answersKey = `${QUESTIONNAIRE_ANSWERS_KEY}:${userId ?? "guest"}`;
      const completedKey = `${QUESTIONNAIRE_ANSWERS_KEY}:${userId ?? "guest"}:completed`;
      const itemsKey = `starlit-checklist-items-v2:${userId ?? "guest"}`;

      window.localStorage.removeItem(answersKey);
      window.localStorage.removeItem(completedKey);
      window.localStorage.removeItem(itemsKey);
    }

    // Delete from database if logged in
    if (userId) {
      try {
        await fetch("/api/questionnaire", { method: "DELETE" });
      } catch (err) {
        console.error("Error deleting questionnaire:", err);
      }
    }

    setAnswers({});
    setViewState("questionnaire");
  }, [userId]);

  // Loading state
  if (viewState === "loading") {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  // Questionnaire view
  if (viewState === "questionnaire") {
    return (
      <div className="space-y-6">
        <header className="space-y-2 text-center">
          <p className="pill inline-flex text-[0.7rem] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            Checklist
          </p>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            Let's create your personalized checklist
          </h1>
          <p className="mx-auto max-w-xl text-sm text-muted-foreground sm:text-base">
            Answer a few questions so we can tailor the checklist to your specific situation. This only takes a minute.
          </p>
        </header>

        <DecisionTreeQuestionnaire userId={userId} initialAnswers={answers} onComplete={handleQuestionnaireComplete} />
      </div>
    );
  }

  // Save prompt for guests
  if (viewState === "save_prompt") {
    const taskCount = generateTaskList(answers).length;

    return (
      <div className="mx-auto max-w-2xl space-y-8 py-8">
        <div className="card-elevated p-8 text-center">
          {/* Success icon */}
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <svg
              className="h-8 w-8 text-primary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h2 className="mb-2 text-2xl font-semibold tracking-tight text-foreground">Your checklist is ready</h2>
          <p className="mb-6 text-muted-foreground">
            We've created a personalized checklist with{" "}
            <span className="font-medium text-foreground">{taskCount} tasks</span> based on your answers. Create an
            account to save your progress and access it from any device.
          </p>

          {/* Benefits */}
          <div className="mb-8 space-y-3 text-left">
            <div className="flex items-start gap-3 rounded-xl bg-secondary/50 p-3">
              <svg
                className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
              <div>
                <p className="text-sm font-medium text-foreground">Your list is saved securely</p>
                <p className="text-xs text-muted-foreground">Access it anytime, from any device</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-xl bg-secondary/50 p-3">
              <svg
                className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-foreground">Track your progress</p>
                <p className="text-xs text-muted-foreground">Check off tasks as you complete them</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-xl bg-secondary/50 p-3">
              <svg
                className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <div>
                <p className="text-sm font-medium text-foreground">Find local vendors</p>
                <p className="text-xs text-muted-foreground">Connect with funeral homes, florists, and more</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <Link
              href={`/sign-up?redirect_url=${encodeURIComponent("/checklist?save_pending=true")}`}
              className="btn btn-primary w-full py-3 text-base"
            >
              Create account to save list
            </Link>
            <button type="button" onClick={handleContinueWithoutSaving} className="btn btn-ghost w-full py-3">
              Continue without saving
            </button>
          </div>

          <p className="mt-4 text-xs text-muted-foreground">
            Already have an account?{" "}
            <Link
              href={`/sign-in?redirect_url=${encodeURIComponent("/checklist?save_pending=true")}`}
              className="font-medium underline underline-offset-4"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // Checklist view
  if (viewState === "checklist") {
    return (
      <PersonalizedChecklist
        userId={userId}
        answers={answers}
        onStartOver={handleStartOver}
        onSaveChecklist={!userId ? handleSaveChecklist : undefined}
      />
    );
  }

  return null;
}
